// routes
import { PATH_DASHBOARD } from "../../../routes/paths";
// components
import SvgColor from "../../../components/svg-color";
// hooks
import { usePatientId } from "../../../hooks/usePatientId";
// auth
import { useAuthContext } from "../../../auth/useAuthContext";
import { Role } from "../../../auth/types/roles";

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor
    src={`/assets/icons/navbar/${name}.svg`}
    sx={{ width: 1, height: 1 }}
  />
);

const ICONS = {
  blog: icon("ic_blog"),
  cart: icon("ic_cart"),
  chat: icon("ic_chat"),
  mail: icon("ic_mail"),
  user: icon("ic_user"),
  file: icon("ic_file"),
  lock: icon("ic_lock"),
  label: icon("ic_label"),
  blank: icon("ic_blank"),
  kanban: icon("ic_kanban"),
  folder: icon("ic_folder"),
  banking: icon("ic_banking"),
  booking: icon("ic_booking"),
  invoice: icon("ic_invoice"),
  calendar: icon("ic_calendar"),
  disabled: icon("ic_disabled"),
  external: icon("ic_external"),
  menuItem: icon("ic_menu_item"),
  ecommerce: icon("ic_ecommerce"),
  analytics: icon("ic_analytics"),
  dashboard: icon("ic_dashboard"),
  till: icon("ic_till"),
  recalls: icon("ic_recalls"),
  reporting: icon("ic_repo"),
  debits: icon("ic_debits"),
};

// -----------------------------------------------------------------------
// Role sets for each nav item — keep in sync with backend RBAC spec.
// null means "visible to every authenticated user".
// -----------------------------------------------------------------------

// Booking/clinical staff who use the diary
const DIARY_ROLES: Role[] = [
  Role.RECEPTIONIST,
  Role.ASSISTANT,
  Role.CLINICIAN,
  Role.PROFESSIONAL,
  Role.MANAGER,
  Role.ACCOUNTANT,
  Role.ADMIN,
  Role.SYS_MANAGER,
];

// Staff who can create dispense orders or take payments at the counter
const DISPENSE_ROLES: Role[] = [
  Role.RECEPTIONIST,
  Role.ASSISTANT,
  Role.MANAGER,
  Role.ACCOUNTANT,
  Role.ADMIN,
  Role.PATIENT, // patient views their own dispense
];

// Same as dispense — till links to payment for the same audience
const TILL_ROLES: Role[] = [
  Role.RECEPTIONIST,
  Role.ASSISTANT,
  Role.MANAGER,
  Role.ACCOUNTANT,
  Role.ADMIN,
  Role.PATIENT, // patient views their own till
];

// Product catalogue visible to front-desk/dispensers (needed for POS workflows)
const PRODUCT_ROLES: Role[] = [
  Role.RECEPTIONIST,
  Role.ASSISTANT,
  Role.MANAGER,
  Role.ACCOUNTANT,
  Role.ADMIN,
];

// Recalls managed by front-desk and marketing staff
const RECALL_ROLES: Role[] = [
  Role.RECEPTIONIST,
  Role.ASSISTANT,
  Role.MANAGER,
  Role.ACCOUNTANT,
  Role.ADMIN,
  Role.MARKETING,
];

// Reporting accessible to clinical and management staff
const REPORTING_ROLES: Role[] = [
  Role.CLINICIAN,
  Role.PROFESSIONAL,
  Role.MANAGER,
  Role.ACCOUNTANT,
  Role.ADMIN,
  Role.SYS_MANAGER,
];

// NHS claims: whoever raises, chases or is answerable for a claim.
const CLAIMS_ROLES: Role[] = [
  Role.RECEPTIONIST,
  Role.ASSISTANT,
  Role.CLINICIAN,
  Role.PROFESSIONAL,
  Role.MANAGER,
  Role.ACCOUNTANT,
  Role.ADMIN,
  Role.SYS_MANAGER,
];

// Direct debits and finance are finance-team/management only
const FINANCE_ROLES: Role[] = [
  Role.MANAGER,
  Role.ACCOUNTANT,
  Role.ADMIN,
  Role.SYS_MANAGER,
];

// ----------------------------------------------------------------------

const useNavConfig = () => {
  const { user } = useAuthContext();
  const { patientId } = usePatientId();

  // Patient role sees their own patient record, others see the list
  const patientPath =
    user?.roleId === Role.PATIENT && patientId
      ? PATH_DASHBOARD.patients.detail(patientId)
      : PATH_DASHBOARD.patients.list;

  // Patient role goes directly to their own dispense record
  const dispensePath =
    user?.roleId === Role.PATIENT
      ? PATH_DASHBOARD.dispense.detail(1)
      : PATH_DASHBOARD.dispense.list;

  // Patient role goes directly to their own till record
  const tillPath =
    user?.roleId === Role.PATIENT && user?.id
      ? PATH_DASHBOARD.till.detail(Number(user.id))
      : PATH_DASHBOARD.till.list;

  return [
    {
      subheader: "management",
      items: [
        {
          title: "diary",
          path: PATH_DASHBOARD.diary.list,
          icon: ICONS.calendar,
          roles: DIARY_ROLES,
        },
        {
          title: "patient",
          path: patientPath,
          icon: ICONS.kanban,
        },
        {
          title: "dispense",
          path: dispensePath,
          icon: ICONS.invoice,
          roles: DISPENSE_ROLES,
        },
        {
          // Sales activity lives in Dispense; bank activity lives in Finance.
          title: "sales",
          path: PATH_DASHBOARD.dispense.sales,
          icon: ICONS.analytics,
          roles: DISPENSE_ROLES,
        },
        {
          title: "till",
          path: tillPath,
          icon: ICONS.till,
          roles: TILL_ROLES,
        },
        {
          title: "product",
          path: PATH_DASHBOARD.product.list,
          icon: ICONS.ecommerce,
          roles: PRODUCT_ROLES,
        },
        {
          title: "recalls",
          path: PATH_DASHBOARD.recalls,
          icon: ICONS.recalls,
          roles: RECALL_ROLES,
        },
        {
          // Chasing what the NHS owes is front-desk and management work alike,
          // and clinicians need to see a claim rejected on their sign-off.
          title: "nhs claims",
          path: PATH_DASHBOARD.claims.list,
          icon: ICONS.file,
          roles: CLAIMS_ROLES,
        },
        {
          title: "reporting",
          path: PATH_DASHBOARD.reporting,
          icon: ICONS.reporting,
          roles: REPORTING_ROLES,
        },
        {
          title: "direct debits",
          path: PATH_DASHBOARD.debits,
          icon: ICONS.debits,
          roles: FINANCE_ROLES,
        },
        {
          title: "finance & vat",
          path: PATH_DASHBOARD.finance,
          icon: ICONS.invoice,
          roles: FINANCE_ROLES,
        },
      ],
    },
  ];
};

export { useNavConfig };
export default useNavConfig;
